/**
 * Login Page
 *
 * Handles user authentication via Firebase (email/password and Google).
 * Calm Authority layout: a dark brand panel (left, desktop only) beside the
 * auth form panel (right). Login / register / reset / resetSent modes are all
 * kept — the split frame wraps the existing form subcomponents unchanged.
 */

import { ShieldCheckIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Logo } from "@/app/shell/Logo";
import { Badge } from "@/components/ui/badge";
import { useLoginAuth } from "@/features/auth/hooks/auth/useLoginAuth";
import AuthNotConfiguredNotice from "@/features/auth/components/AuthNotConfiguredNotice";
import LoginForm from "@/features/auth/components/LoginForm";
import LoginGoogleSignIn from "@/features/auth/components/LoginGoogleSignIn";
import LoginHeader from "@/features/auth/components/LoginHeader";
import LoginModeSwitcher from "@/features/auth/components/LoginModeSwitcher";
import LoginResetSentState from "@/features/auth/components/LoginResetSentState";
import LoginSessionLoading from "@/features/auth/components/LoginSessionLoading";

export default function Login() {
  const { t } = useTranslation(["auth", "common"]);
  const auth = useLoginAuth();

  if (!auth.isConfigured) {
    return <AuthNotConfiguredNotice />;
  }

  if (auth.user && auth.loading) {
    return <LoginSessionLoading />;
  }

  return (
    <div
      className="flex min-h-[100dvh] flex-col lg:flex-row"
      data-test="login-page"
    >
      {/* Brand panel — deep-ink, desktop only */}
      <aside
        className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[var(--nav-bg)] p-12 text-[var(--nav-ink)] lg:flex"
        data-test="login-brand-panel"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 90% at 15% 10%, color-mix(in srgb, var(--nav-brand) 35%, transparent), transparent 60%)",
          }}
        />

        <div className="relative flex items-center gap-3">
          <Logo size={38} />
          <div className="leading-tight">
            {/* eslint-disable-next-line i18next/no-literal-string -- brand wordmark, not translatable */}
            <div className="font-heading text-lg font-bold text-[var(--nav-ink)]">Vardic</div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-[var(--nav-ink-faint)]">
              {t("common:brand.tagline")}
            </div>
          </div>
        </div>

        <div className="relative max-w-md space-y-8">
          <Badge
            variant="secondary"
            className="gap-1.5 bg-[var(--nav-bg-2)] text-[var(--nav-ink)]"
            data-test="login-hero-badge"
          >
            <ShieldCheckIcon />
            {t("auth:marketing.badge")}
          </Badge>
          <div className="space-y-4">
            <h1 className="font-heading text-4xl font-semibold leading-tight tracking-tight">
              {t("auth:marketing.title")}
            </h1>
            <p className="text-base leading-7 text-[var(--nav-ink-2)]">
              {t("auth:marketing.subtitle")}
            </p>
          </div>
        </div>

      </aside>

      {/* Form panel */}
      <main className="flex flex-1 items-center justify-center bg-[var(--background)] px-4 py-12 sm:px-6">
        <div className="w-full max-w-md">
          {/* Mobile-only brand mark (brand panel is desktop-only) */}
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <Logo size={32} />
            {/* eslint-disable-next-line i18next/no-literal-string -- brand wordmark, not translatable */}
            <span className="font-heading text-lg font-bold text-[var(--foreground)]">
              Vardic
            </span>
          </div>

          <LoginHeader mode={auth.mode} />

          {auth.mode !== "reset" && (
            <LoginGoogleSignIn
              isSubmitting={auth.isSubmitting}
              onSignIn={auth.handleGoogleSignIn}
            />
          )}

          {auth.resetSent
            ? (
              <LoginResetSentState
                email={auth.email}
                onBackToSignIn={() => auth.switchMode("login")}
              />
            )
            : (
              <LoginForm
                mode={auth.mode}
                email={auth.email}
                password={auth.password}
                confirmPassword={auth.confirmPassword}
                name={auth.name}
                signupSource={auth.signupSource}
                error={auth.error}
                isSubmitting={auth.isSubmitting}
                onSubmit={auth.handleSubmit}
                onEmailChange={auth.setEmail}
                onPasswordChange={auth.setPassword}
                onConfirmPasswordChange={auth.setConfirmPassword}
                onNameChange={auth.setName}
                onSignupSourceChange={auth.setSignupSource}
              />
            )}

          <LoginModeSwitcher
            mode={auth.mode}
            resetSent={auth.resetSent}
            onSwitchMode={auth.switchMode}
          />
          <p className="mt-8 text-xs leading-5 text-[var(--muted-foreground)]">
            {t("auth:marketing.disclaimer")}
          </p>
        </div>
      </main>
    </div>
  );
}
